import React from 'react';
import { useScrollAnimation, useStaggerAnimation } from '../hooks/useScrollAnimation';

/**
 * Animated section wrapper - fades in when scrolled into view
 */
export const AnimatedSection = ({ 
  children, 
  className = '', 
  animation = 'fade-up', // fade, fade-up, fade-down, fade-left, fade-right, scale
  delay = 0,
  ...props 
}) => {
  const [ref, isVisible] = useScrollAnimation();
  
  const animationClass = `animate-${animation}`;
  const delayStyle = delay ? { transitionDelay: `${delay}ms` } : {};
  
  return (
    <div 
      ref={ref} 
      className={`${animationClass} ${isVisible ? 'visible' : ''} ${className}`}
      style={delayStyle}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Animated card with hover effects
 */
export const AnimatedCard = ({ 
  children, 
  className = '', 
  hoverEffect = 'lift', // lift, lift-subtle, none
  delay = 0,
  ...props 
}) => {
  const [ref, isVisible] = useScrollAnimation();
  
  const hoverClass = hoverEffect !== 'none' ? `hover-${hoverEffect}` : '';
  const delayStyle = delay ? { transitionDelay: `${delay}ms` } : {};
  
  return (
    <div 
      ref={ref} 
      className={`animate-fade-up ${isVisible ? 'visible' : ''} ${hoverClass} hover-zoom ${className}`}
      style={delayStyle}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Staggered grid - children animate in sequence
 */
export const StaggeredGrid = ({ 
  children, 
  className = '', 
  staggerDelay = 100,
  ...props 
}) => {
  const [ref, isVisible] = useScrollAnimation();
  
  return (
    <div ref={ref} className={className} {...props}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        
        return React.cloneElement(child, {
          className: `${child.props.className || ''} animate-fade-up ${isVisible ? 'visible' : ''}`,
          style: {
            ...child.props.style,
            transitionDelay: `${index * staggerDelay}ms`
          }
        });
      })}
    </div>
  );
};

/**
 * Animated text reveal
 */
export const AnimatedText = ({ 
  children, 
  as: Component = 'div',
  className = '',
  delay = 0,
  ...props 
}) => {
  const [ref, isVisible] = useScrollAnimation();
  const delayStyle = delay ? { transitionDelay: `${delay}ms` } : {};
  
  return (
    <Component 
      ref={ref} 
      className={`animate-fade-up ${isVisible ? 'visible' : ''} ${className}`}
      style={delayStyle}
      {...props}
    >
      {children}
    </Component>
  );
};

/**
 * Animated counter (counts up when visible)
 */
export const AnimatedCounter = ({ 
  end, 
  duration = 2000, 
  suffix = '',
  prefix = '',
  className = '' 
}) => {
  const [ref, isVisible] = useScrollAnimation();
  const [count, setCount] = React.useState(0);
  
  React.useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);
  
  return (
    <span ref={ref} className={`counter-animate ${className}`}>
      {prefix}{count}{suffix}
    </span>
  );
};

export default AnimatedSection;
